import { useState, useEffect, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { GET_USER_LIKES } from "@/graphql/queries/user";
import { useAuthState } from "@/contexts/auth/auth.provider";
import {
  useFavListingsDispatch,
  useFavListingsState,
} from "@/contexts/fav-listings/favListings.provider";
import logger from "@/console";
import { ADD_LIKE, REMOVE_LIKE } from "@/graphql/mutations/user";
import actions from "@/contexts/fav-listings/favListings.actions";

const { addLike, removeLike } = actions;

const useGetAllUserLikes = (limit = 100) => {
  const [fetchCompleted, setFetchCompleted] = useState(false);
  const { token } = useAuthState();
  const [error, setError] = useState(null);

  const { loading, data, fetchMore } = useQuery(GET_USER_LIKES, {
    variables: { limit, offset: null },
    context: { token },
    skip: !token,
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      if (data?.User?.getUserLikes?.offset === null) {
        setFetchCompleted(true);
      } else if (!fetchCompleted && data?.User?.getUserLikes?.offset) {
        fetchMore({
          variables: { offset: data.User.getUserLikes.offset, limit },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;

            const newLikes = fetchMoreResult.User.getUserLikes.likes;
            const newOffset = fetchMoreResult.User.getUserLikes.offset;

            if (newOffset === null) {
              setFetchCompleted(true);
            }

            return {
              User: {
                ...prev.User,
                getUserLikes: {
                  ...prev.User.getUserLikes,
                  likes: [...prev.User.getUserLikes.likes, ...newLikes],
                  offset: newOffset,
                },
              },
            };
          },
        }).catch((err) => {
          setError(err);
          setFetchCompleted(true);
        });
      }
    },
    onError: (err) => {
      setError(err);
      setFetchCompleted(true);
    },
    fetchPolicy: "network-only",
  });

  const likes = data?.User?.getUserLikes?.likes || [];

  const likesMap = useMemo(() => {
    return likes.reduce((acc, like) => {
      acc[like.listing_id] = like;
      return acc;
    }, {});
  }, [likes]);

  if (!token) {
    return {
      loading: false,
      error: null,
      likesMap: {},
      likes: [],
      fetchCompleted: true,
    };
  }

  return { loading, error, likesMap, likes, fetchCompleted };
};

export function useToggleLike(listingID) {
  const dispatchFavListing = useFavListingsDispatch();
  const token = useAuthState("token");
  const { userLikes } = useFavListingsState();
  const [isLiked, setIsLiked] = useState(!!userLikes[listingID]);

  function getLikeMutation() {
    return isLiked ? REMOVE_LIKE : ADD_LIKE;
  }

  const [toggleLikeMutation, { loading }] = useMutation(getLikeMutation(), {
    context: { token },
    variables: { listing_id: parseInt(listingID, 10) },
    onCompleted: (data) => {
      if (data?.User?.addLike) {
        return dispatchFavListing(addLike(listingID));
      }
      if (data?.User?.removeLike) {
        dispatchFavListing(removeLike(listingID));
      }
    },
    onError: (err) => {
      logger.error(err.message);
      setIsLiked(!isLiked);
    },
  });

  useEffect(() => {
    setIsLiked(!!userLikes[listingID]);
  }, [userLikes, listingID]);

  const toggleLike = () => {
    setIsLiked(!isLiked);
    toggleLikeMutation();
  };

  return { isLiked, toggleLike, loading };
}

export default useGetAllUserLikes;
